import removeSelectedFilter from '@/catalog/utils/remove-selected-filter';
import { useCallback } from 'react';
import { FilterCategory, IndexedFilters, FilterPrimitive } from '../types/filters';

interface CheckboxTogglesProps {
  /** Name of the filter category, e.g. "body_type",  "exterior_color", etc. */
  categoryName: FilterCategory;
  /** A setState function to update selectedFilters. */
  setSelectedFilters: React.Dispatch<React.SetStateAction<IndexedFilters>>;
}

interface CheckboxTogglesResponse<T = FilterPrimitive> {
  toggleCheckbox: (checked: boolean, value: T | T[]) => void;
}

/**
 * Custom hook that provides toggle handlers for checkbox groups.
 *
 * Note: We do `prev[categoryName] as T[]`, but we
 * can assume that the category value is a T[] because of
 * the Type Narrowing that happens in FilterMenuCategory.
 */
export const useCheckboxToggles = <T extends FilterPrimitive>({
  categoryName,
  setSelectedFilters,
}: CheckboxTogglesProps): CheckboxTogglesResponse<T> => {
  /**
   * Adds the selected filter value to the list.
   * Triggered when a checkbox is selected.
   */
  const addFilter = useCallback(
    (value: T | T[]) => {
      setSelectedFilters((prev) => {
        const currentValues = prev[categoryName] as T[] | undefined;

        if (Array.isArray(value)) {
          // If filter values are all already selected, don't make any changes.
          if (value.every((v) => currentValues?.includes(v))) return prev;

          // Filter out any values that are already selected.
          const updatedValues = value
            .filter((v) => !currentValues?.includes(v))
            .concat(currentValues ?? []);

          return { ...prev, [categoryName]: updatedValues };
        } else {
          // If the filter value is already selected, don't make any changes.
          if (currentValues?.includes(value)) return prev;

          // Append the filter value to the existing list or create a new list.
          const updatedValues = currentValues ? [...currentValues, value] : [value];

          return { ...prev, [categoryName]: updatedValues };
        }
      });
    },
    [categoryName, setSelectedFilters]
  );

  /**
   * Removes the deselected filter value from the list.
   * Triggered when a checkbox is deselected.
   */
  const removeFilter = useCallback(
    (value: T | T[]) => {
      setSelectedFilters((prev) =>
        removeSelectedFilter({ key: categoryName, value: value }, prev)
      );
    },
    [categoryName, setSelectedFilters]
  );

  /**
   * Handles the toggling of individual checkboxes.
   */
  const toggleCheckbox = useCallback(
    (checked: boolean, value: T | T[]) => {
      // Based on the checkbox state, either add or remove the filter value.
      if (checked) {
        removeFilter(value);
      } else {
        addFilter(value);
      }
    },
    [addFilter, removeFilter]
  );

  return { toggleCheckbox };
};
